import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AlterarSociedadeDto, Pagina, Pessoa, Sociedade } from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { Spinner } from '../componentes/carregando';
import { Aviso, Botao, Campo, Entrada } from '../componentes/ui';
import { ErroApi } from '../lib/api';
import { api } from '../lib/sessao';
import { CamposDaSociedade } from './sociedade-campos';
import { cessoesParaDto, SecaoCessoes, type RascunhoCessao } from './sociedade-cessoes';
import {
  capitalDerivado,
  centavosDeReais,
  hojeISO,
  montarDto,
  rascunhoDe,
  type RascunhoSociedade,
} from './sociedade-nova-comum';
import { Secao } from './sociedade-nova-secao';

/*
  A ALTERAÇÃO contratual — o trabalho recorrente do contador.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Mudou alguma coisa nesta sociedade, e
     preciso do documento para levar à Junta." Ela não vem explorar; vem
     corrigir um dado específico que já sabe qual é.
  2. **Quantos registros?** Um: esta sociedade. Sem lista, sem busca, sem
     paginação.
  3. **Resultado vs aparência?** Nada filtra nada aqui.
  4. **Barra ou gaveta?** Não se aplica.
  5. **375px?** O mesmo do cadastro — os campos empilham numa coluna, e é o
     `CamposDaSociedade` que já resolve isso para as duas telas.
  6. **O que promete e não existe?** Nada. A tela abre com o estado atual, e o
     que ela envia é o estado novo por inteiro — o servidor compara e descobre
     o que mudou (`compararSociedades`). Não há aqui nenhum botão levando a
     coisa que não existe.

  ## Por que ela começa PREENCHIDA
  Porque a alteração não é um cadastro novo. Abrir em branco obrigaria a
  redigitar sócios, capital e objeto social para mudar um endereço — e cada
  redigitação é uma chance de alterar sem querer um campo que ninguém pediu
  para alterar. Pior: essa alteração acidental sairia no documento, com força
  de ato registrado.
*/

export function SociedadeAlterar(): JSX.Element {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const fila = useQueryClient();

  const sociedade = useQuery({
    queryKey: ['sociedade', id] as const,
    queryFn: () => api.get<Sociedade>(`/sociedades/${id}`),
  });

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });
  const pessoas = pessoasQuery.data?.itens ?? [];

  const [r, setR] = useState<RascunhoSociedade | null>(null);
  /*
    A data do INSTRUMENTO, separada do rascunho: ela não é um dado da
    sociedade, é do ato. A alteração costuma ser assinada num dia e lançada no
    sistema noutro, e é a data do instrumento que a Junta lê — usar "hoje"
    calado produziria um documento com a data errada em toda alteração
    lançada com atraso.
  */
  const [dataDoAto, setDataDoAto] = useState(hojeISO());
  const [cessoes, setCessoes] = useState<RascunhoCessao[]>([]);

  const original = sociedade.data;
  useEffect(() => {
    if (original) setR(rascunhoDe(original));
  }, [original]);

  const valorNominal = r ? centavosDeReais(r.valorNominalQuota) : 0;
  const capital = useMemo(
    () =>
      r
        ? capitalDerivado(
            r.socios.map((s) => Number(s.quotas)),
            valorNominal,
          )
        : 0,
    [r, valorNominal],
  );

  const salvar = useMutation({
    mutationFn: (dto: AlterarSociedadeDto) =>
      api.post<Sociedade>(`/sociedades/${id}/alteracao`, dto),
    onSuccess: async () => {
      await fila.invalidateQueries({ queryKey: ['sociedades'] });
      await fila.invalidateQueries({ queryKey: ['sociedade', id] });
      // Volta para o detalhe: quem acabou de alterar quer conferir o ato novo
      // e gerar o documento, que é o motivo de ter alterado.
      navegar(`/sociedades/${id}`);
    },
  });

  const mudar = (parcial: Partial<RascunhoSociedade>) =>
    setR((atual) => (atual ? { ...atual, ...parcial } : atual));

  const enviar = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!r) return;
    salvar.mutate({
      ...montarDto(r, valorNominal, capital),
      data: dataDoAto,
      cessoes: cessoesParaDto(cessoes),
    });
  };

  if (sociedade.isPending || !r) {
    return (
      <PaginaShell titulo="Alterar sociedade">
        <p className="text-tinta-500 flex items-center gap-2 text-sm">
          <Spinner /> Carregando a sociedade…
        </p>
      </PaginaShell>
    );
  }

  if (sociedade.isError) {
    return (
      <PaginaShell titulo="Alterar sociedade">
        <Aviso>Não foi possível abrir esta sociedade. Volte e tente de novo.</Aviso>
      </PaginaShell>
    );
  }

  return (
    <PaginaShell
      titulo="Alterar sociedade"
      subtitulo={`${original?.nomeEmpresarial ?? ''} · cadastro #${String(original?.codigo ?? '')}`}
      acoes={
        <Link
          to={`/sociedades/${id}`}
          className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Voltar
        </Link>
      }
    >
      <form onSubmit={enviar} className="flex flex-col gap-4" noValidate>
        {salvar.isError && <Aviso>{mensagemDoErro(salvar.error)}</Aviso>}

        <Secao
          titulo="O ato"
          descricao="Altere abaixo o que mudou. O sistema compara com o cadastro atual e monta a alteração contratual só com as diferenças."
        >
          <Campo
            rotulo="Data do instrumento"
            id="dataDoAto"
            obrigatorio
            dica="A data em que a alteração foi assinada — é ela que vai no documento."
          >
            <Entrada
              id="dataDoAto"
              type="date"
              value={dataDoAto}
              onChange={(e) => setDataDoAto(e.target.value)}
            />
          </Campo>
        </Secao>

        <CamposDaSociedade
          rascunho={r}
          mudar={mudar}
          pessoas={pessoas}
          capital={capital}
          valorNominal={valorNominal}
        />

        <SecaoCessoes
          cessoes={cessoes}
          aoMudar={setCessoes}
          pessoas={pessoas}
          sociosAtuais={r.socios.map((x) => x.pessoaId).filter(Boolean)}
        />

        <div className="border-tinta-100 flex items-center justify-end gap-2 border-t pt-4">
          <Link
            to={`/sociedades/${id}`}
            className="text-tinta-700 hover:bg-marca-600/10 inline-flex h-11 items-center rounded-lg px-4 text-sm transition-colors"
          >
            Cancelar
          </Link>
          <Botao type="submit" carregando={salvar.isPending}>
            <Save className="size-4" aria-hidden />
            Gerar alteração
          </Botao>
        </div>
      </form>
    </PaginaShell>
  );
}

function mensagemDoErro(erro: unknown): string {
  // A mensagem do servidor é melhor que qualquer texto genérico daqui: ela diz
  // "nada mudou em relação ao cadastro atual" ou nomeia a regra que reprovou,
  // e é isso que a pessoa precisa para saber o que fazer em seguida.
  if (erro instanceof ErroApi) return erro.message;
  return 'Não foi possível gerar a alteração. Tente de novo.';
}
